import {
  CheckIcon,
  ChecksIcon,
  HourglassIcon,
  PaperPlaneRightIcon,
  TruckIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Input } from 'antd';
import { useState } from 'react';

import { Chip } from '@/components/common/chip';
import { ImageAttach, type AttachedImage } from '@/components/common/image-attach';
import { Drawer } from '@/components/feedback/drawer';
import { ErrorState } from '@/components/feedback/error-state';
import { useToast } from '@/components/feedback/toast';
import { formatIsoDate } from '@/lib/utils/format';

import {
  MAX_PHOTOS,
  REASON_LABELS,
  REJECTION_REASON_LABELS,
  RESPONSE_MAX,
  WARRANTY_STATUS_META,
} from '../constants';
import { useRespondToClaim, useWarrantyClaim } from '../hooks';
import type { ClaimComment, WarrantyClaimDetail, WarrantyClaimStatus } from '../types';

import styles from './warranty-claim-detail-drawer.module.scss';
import { WarrantyClaimDetailSkeleton } from './warranty-claim-detail-skeleton';

export type WarrantyClaimDetailDrawerProps = {
  open: boolean;
  claimId: string | null;
  onClose: () => void;
};

/** Phosphor icon shown in the timeline node disc for a claim status (Figma 2715-811213). */
function statusIcon(status: WarrantyClaimStatus) {
  switch (status) {
    case 'approved':
    case 'closed':
      return <ChecksIcon size={14} />;
    case 'under_review':
    case 'info_requested':
      return <HourglassIcon size={14} />;
    case 'rejected':
      return <XIcon size={14} />;
    default:
      return <CheckIcon size={14} />;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{children}</span>
    </div>
  );
}

function CommentRow({ comment }: { comment: ClaimComment }) {
  const mine = comment.actor === 'grower';
  return (
    <div className={`${styles.comment} ${mine ? styles.commentMine : ''}`}>
      <div className={styles.commentHead}>
        <span className={styles.commentAuthor}>{comment.authorName}</span>
        <span className={styles.commentTime}>{formatIsoDate(comment.timestamp)}</span>
      </div>
      <p className={styles.commentBody}>{comment.body}</p>
    </div>
  );
}

/**
 * Claim detail drawer (Figma 2715-419630). Shows the reference + status, order id, reason,
 * description, submitted evidence, replacement shipment (when applicable), the Admin/grower
 * comment thread, and the status timeline. When the claim is Info Requested, a composer lets
 * the grower reply — returning the claim to Under Review (stories 101872 + 101874).
 */
export function WarrantyClaimDetailDrawer({
  open,
  claimId,
  onClose,
}: WarrantyClaimDetailDrawerProps) {
  const toast = useToast();
  const query = useWarrantyClaim(claimId ?? '', open && claimId != null);
  const claim = query.data;

  return (
    <Drawer open={open} onClose={onClose} cancelText="" width={480}>
      {query.isError ? (
        <ErrorState
          title="Couldn't load this claim"
          description="There was a problem reaching the server."
          onRetry={() => void query.refetch()}
        />
      ) : query.isLoading || !claim ? (
        <WarrantyClaimDetailSkeleton />
      ) : (
        <DetailBody claim={claim} toast={toast} />
      )}
    </Drawer>
  );
}

function DetailBody({
  claim,
  toast,
}: {
  claim: WarrantyClaimDetail;
  toast: ReturnType<typeof useToast>;
}) {
  const respond = useRespondToClaim(claim.id);
  const meta = WARRANTY_STATUS_META[claim.status];

  const [body, setBody] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const uploading = images.some((img) => img.status === 'uploading');
  const canSend = body.trim().length > 0 && !uploading && !respond.isPending;

  const reasonText =
    claim.reason === 'other' ? (claim.reasonOther ?? 'Other') : REASON_LABELS[claim.reason];

  // Timeline newest-first (current step on top), matching the Figma drawer.
  const timelineEvents = [...claim.timeline].reverse();

  const send = () => {
    if (!canSend) return;
    respond.mutate(
      { body: body.trim(), images: images.map((img) => img.name) },
      {
        onSuccess: () => {
          setBody('');
          setImages([]);
          toast.success({
            title: 'Response sent',
            description: 'Your claim is back under review.',
          });
        },
        onError: () =>
          toast.error({ title: 'Could not send response', description: 'Please try again.' }),
      },
    );
  };

  return (
    <div className={styles.body}>
      <div className={styles.head}>
        <div className={styles.refRow}>
          <span className={styles.ref}>#{claim.ref}</span>
          <Chip intent={meta.intent}>{meta.label}</Chip>
        </div>
        <h2 className={styles.device}>{claim.deviceName}</h2>
      </div>

      <Field label="Order ID">{claim.orderId}</Field>
      <Field label="Reason">{reasonText}</Field>
      <Field label="Description">{claim.description}</Field>
      {claim.status === 'rejected' && claim.rejectionReason ? (
        <Field label="Rejection Reason">{REJECTION_REASON_LABELS[claim.rejectionReason]}</Field>
      ) : null}

      {claim.images.length > 0 ? (
        <div className={styles.field}>
          <span className={styles.attachLabel}>Attachments</span>
          <div className={styles.thumbs}>
            {claim.images.map((name, i) => (
              <span className={styles.thumb} key={`${name}-${i}`}>
                {claim.imageUrl ? <img src={claim.imageUrl} alt={name} /> : null}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {claim.comments.length > 0 ? (
        <div className={styles.thread}>
          {claim.comments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </div>
      ) : null}

      {claim.shipment || timelineEvents.length > 0 ? (
        <ol className={styles.timeline}>
          {claim.shipment ? (
            <li className={styles.timelineItem}>
              <span className={styles.timelineIcon}>
                <TruckIcon size={14} />
              </span>
              <div className={styles.timelineText}>
                <span className={styles.timelineTitle}>Replacement in progress</span>
                <span className={styles.timelineSub}>
                  Expected Delivery{' '}
                  <span className={styles.deliveryDate}>
                    {formatIsoDate(claim.shipment.expectedDeliveryAt)}
                  </span>
                </span>
              </div>
              <span className={styles.trackingId}>Tracking ID {claim.shipment.trackingNumber}</span>
            </li>
          ) : null}
          {timelineEvents.map((event, i) => (
            <li className={styles.timelineItem} key={`${event.status}-${i}`}>
              <span className={styles.timelineIcon}>{statusIcon(event.status)}</span>
              <div className={styles.timelineText}>
                <span className={styles.timelineTitle}>{event.label}</span>
                <span className={styles.timelineDate}>{formatIsoDate(event.timestamp)}</span>
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {claim.status === 'info_requested' ? (
        <div className={styles.composer}>
          <span className={styles.composerLabel}>Respond to this request</span>
          <Input.TextArea
            value={body}
            maxLength={RESPONSE_MAX}
            rows={3}
            placeholder="Add the requested information…"
            onChange={(e) => setBody(e.target.value)}
          />
          <ImageAttach value={images} onChange={setImages} maxCount={MAX_PHOTOS} />
          <button type="button" className={styles.send} disabled={!canSend} onClick={send}>
            <PaperPlaneRightIcon size={16} weight="fill" />
            {respond.isPending ? 'Sending…' : 'Send Response'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
